/**
 * Spoke IP Masking Utility
 *
 * Replaces architecture-revealing terminology with safe public equivalents
 * on Spoke deployments. Hub sees full technical details.
 */

// Architecture term → safe public term mapping
const TERM_MAP: Record<string, string> = {
  // Algorithms
  'EWC Fisher-information matrix': 'parameter importance map',
  'EWC / Fisher': 'Stability Protection',
  'EWC/Fisher': 'Stability Protection',
  'EWC Fisher': 'Stability Protection',
  'EWC': 'Stability Protection',
  'Elastic Weight Consolidation': 'Knowledge Stability Protection',
  'Fisher Information Matrix': 'Parameter Importance Analysis',
  'Fisher Information': 'Parameter Importance',
  'Fisher Matrices': 'Stability Parameters',
  'Fisher Computed': 'Importance Computed',
  'Fisher Value': 'Importance Value',
  'Fisher information': 'parameter importance',
  'Fisher matrices': 'stability parameters',
  'EWC Lambda': 'Protection Strength',
  'EWC Active': 'Protection Active',
  'EWC Retraining': 'Stability-Aware Retraining',
  'MAML': 'Adaptive Learning',
  'MAML meta-learning': 'adaptive self-improvement',
  'MAML outer-loop': 'adaptive optimization',
  'MAML Status': 'Adaptive Learning Status',
  'UCB1': 'Priority',
  'UCB1-bandit': 'priority-based',
  'UCB Score': 'Priority Score',
  'UCB1 bandit': 'priority-based',
  'Pareto frontier': 'optimization frontier',
  'Pareto Frontier': 'Optimization Frontier',
  'Pareto-Optimal': 'Optimal',
  'Monte Carlo Tree Search (MCTS)': 'AI Planning Search',
  'Monte Carlo Tree Search': 'AI Planning Search',
  'MCTS planning': 'AI-driven planning',
  'MCTS': 'AI Planning',

  // Neural architectures
  'GAN Augmentation': 'Synthetic Data Generation',
  'GAN augmentation': 'synthetic data generation',
  'GAN-augmented': 'synthetically enhanced',
  'Conditional GAN': 'Conditional Generator',
  'VAE+GAN': 'Generative',
  'SMOTE': 'oversampling',

  // Physics/Math
  'ODE': 'Simulation',
  'PDE': 'Simulation',
  'ODE/PDE': 'Physics Simulation',
  'ODE-based': 'simulation-based',
  'ODE Steps': 'Simulation Steps',
  'RL-optimized': 'AI-optimized',
  'Reinforcement learning': 'Adaptive optimization',
  'reinforcement learning': 'adaptive optimization',

  // ML techniques
  'SHAP': 'Feature Impact',
  'SHAP Feature Explorer': 'Feature Impact Explorer',
  'SHAP explanations': 'feature impact explanations',
  'SHAP values': 'feature impact values',
  'SHAP data': 'feature impact data',
  'SHAP explanation': 'feature impact explanation',
  'SHAP feature': 'feature impact',
  'SHAP context': 'impact context',
  'Raw SHAP Values': 'Raw Impact Values',

  // LLM specific
  'Ollama': 'AI Engine',
  'Qwen-VL': 'Vision AI',
  'qwen2.5:3b': 'Local AI Model',

  // Continual learning
  'catastrophic forgetting': 'knowledge loss',
  'anti-catastrophic-forgetting': 'knowledge-retention',
  'Prioritized experience replay': 'Smart experience replay',
  'Hippocampal memory': 'Long-term memory',
  'episodic replay': 'experience replay',

  // Brain architecture
  'Corpus Callosum': 'Brain Fusion',
  'corpus callosum': 'brain fusion',
  'left-right brain fusion': 'multi-model fusion',
  'Left-Right Brain Fusion': 'Multi-Model Fusion',
  'left-right brain': 'multi-model fusion',
  'Left-Right Brain': 'Multi-Model Fusion',
  'Neuro-Symbolic Rules': 'Reasoning Rules',
  'Neuro-Symbolic Verifier': 'Reasoning Verifier',
  'Neuro-Symbolic': 'Intelligent Reasoning',
  'neuro-symbolic': 'intelligent reasoning',
  'MetaCognition Gate': 'Decision Gate',
  'metacognition gate': 'decision gate',

  // ─── IP leaks discovered in V1 screenshot audit (2026-04-20) ──────

  // Digital Twin / simulation architecture (Behavior Simulator page)
  '3-Tier Digital Twin': 'Digital Twin',
  '3-Tier': 'Multi-Layer',
  'Physics Engine': 'Simulation Core',
  'Causal Engine': 'Cause-Effect Analyzer',
  'RL Optimizer': 'AI Optimizer',
  'ML Prediction': 'AI Forecast',
  'First-principles ODE/PDE simulation': 'Equation-grounded simulation',
  'First-principles': 'Equation-grounded',
  'Do-calculus intervention analysis': 'Intervention analysis',
  'Do-calculus': 'Intervention logic',
  'do-calculus': 'intervention logic',
  'Reinforcement learning control': 'Adaptive control',
  'RL-Powered Parameter Optimization': 'AI-Powered Parameter Optimization',
  'reinforcement learning over physics environment': 'adaptive optimization against the simulator',
  'Policy Network': 'Control Model',
  'Neural network': 'AI model',
  'Reward Signal': 'Objective Score',

  // Brain topology (Confidence Gate / Cognitive Twin)
  'Left Brain': 'Analytical Layer',
  'Right Brain': 'Generative Layer',
  'left brain': 'analytical layer',
  'right brain': 'generative layer',
  'Left Brain (Analytical)': 'Analytical Layer',
  'Right Brain (Creative)': 'Generative Layer',
  'both hemispheres': 'both reasoning layers',
  'hemispheres': 'reasoning layers',
  'Statistical models, pattern recognition, deterministic reasoning': 'Analytical rigor + pattern recognition',
  'Heuristic reasoning, causal inference': 'Exploratory reasoning + cause-and-effect logic',
  'Imagination/Dreaming': 'Scenario Imagination',
  'Hypothesis Generator': 'Idea Generator',
  'Attention Allocator': 'Priority Allocator',
  'Red-Team Agent': 'Robustness Tester',

  // Adversarial ML taxonomy (Robustness Shield)
  'FGSM Fast Gradient': 'Gradient-based probe',
  'FGSM (Fast Gradient)': 'Gradient-based probe',
  'FGSM': 'Gradient probe',
  'PGD Projected Gradient': 'Iterative gradient probe',
  'PGD (Projected Gradient)': 'Iterative gradient probe',
  'PGD': 'Iterative probe',
  'Boundary Attack': 'Boundary probe',
  'Noise Injection': 'Noise probe',
  'Out-of-Distribution': 'Unseen-input probe',
  'Label Poisoning': 'Training-data probe',
  'Backdoor Trigger': 'Hidden-trigger probe',
  'Evasion Attack': 'Evasion probe',
  'adversarial ML': 'robustness testing',

  // Weekly AI Upgrade / Meta-Orchestra weights. Mask to "Priority Router"
  // (no remaining architecture tokens) rather than to "Thinking-Depth".
  'EffortRouter Weight Distribution': 'Priority Weight Distribution',
  'EffortRouter': 'Priority Router',
  'MAML weight updates': 'Adaptive weight updates',
  'MAML meta-training': 'Adaptive self-training',

  // Drift detection terminology (Self-Evolution)
  'KS-test': 'distribution-shift test',
  'KS-test, Wasserstein, PSI': 'multi-signal drift detection',
  'Wasserstein': 'distribution-distance',
  'PSI mean-shift': 'population-shift',
  'PSI': 'population-shift',
  'F1/AUC': 'accuracy metrics',
  'Bayesian expected utility': 'probability-weighted value',
  'Conditional Generator + oversampling fallback': 'synthetic-data fallback',

  // Discovery / Synthetic data
  'GAN-augmented synthetic data': 'AI-generated synthetic data',

  // Model IDs / provider leaks
  'generic/qwen2.5:3b': 'Local AI Engine',
  'qwen2.5': 'Local AI Engine',
  'generic/': '',

  // Calibration internals
  'ECE (lower=better)': 'Calibration Error (lower is better)',
  'MCE (lower=better)': 'Peak Calibration Error (lower is better)',
  'Temperature 1.00': 'Confidence scaling 1.00×',

  // Hybrid pipeline references
  'Multi-Head architecture': 'Multi-output model',
  'Hybrid pipeline multi-head model': 'Multi-output AI model',
  'Multi-head': 'Multi-output',
  'hybrid pipeline': 'AI pipeline',

  // Neuro-symbolic cascades
  'neuro-symbolic cascades': 'rule-guided reasoning chains',
  'neuro-symbolic engine': 'rule-guided reasoning engine',

  // ─── Architecture / branding leaks surfaced in V3 screenshots (Apr 24) ──
  'Brain Intelligence': 'AI Engine',
  'brain intelligence': 'AI engine',
  'AI-Driven Industrial Analytics': 'Industrial AI',
  'Brain page': 'Page',
  'Brain pages': 'Pages',
  'Brain Scorecard': 'System Scorecard',
  'Brain Engine': 'AI Engine',
  'Brain engine': 'AI engine',
  'Brain Security': 'Robustness Testing',
  'Brain Dashboard': 'AI Dashboard',
  'Brain Health': 'System Health',
  'Brain Map': 'System Map',
  'Brain layer': 'AI layer',
  'Agentic Oracle': 'AI Assistant',
  'agentic oracle': 'AI assistant',
  'Oracle is listening': 'AI Assistant',
  'Oracle combines': 'AI Assistant combines',
  'Oracle your AI co-pilot': 'AI Assistant',
  'Ask Oracle': 'Ask AI',
  'Oracle\'s most recent': 'AI\'s most recent',
  'Ask AI': 'Ask AI',
  'Oracle Whisper': 'AI Hint',
  'AGI': 'AI',
  'agi_orchestrator': 'ai_orchestrator',
  'AGI Chat': 'AI Chat',
  'Learning Context': 'Context',
  'Chain Reaction': 'Impact Chain',
  'Chain: ': 'Context: ',
  'MetaCognition': 'Decision Oversight',
  'metacognition': 'decision oversight',
  'Dreaming Engine': 'Scenario Engine',
  'dreaming engine': 'scenario engine',
  'Meta-Orchestra': 'Adaptive Optimizer',
  'meta-orchestra': 'adaptive optimizer',
  'MAML outer loop': 'adaptive optimization',
  'self-evolution': 'auto-improvement',
  'Self-Evolution': 'Auto-Improvement',
  'Self-Healing': 'Auto-Recovery',
  'self-healing': 'auto-recovery',
  'brainPageRegistry.ts': 'page registry',
  'brainpageregistry.ts': 'page registry',
  'Episodic memory': 'Experience log',
  'episodic memory': 'experience log',

  // ─── Phase 11 additions (Apr 25 2026) — plug V3 sidebar + hero leaks ──
  // Bare "Oracle" token in sidebar + related surfaces. Longer "Ask Oracle",
  // "Oracle is listening" variants are matched first by length-desc sort.
  'Oracle': 'AI Assistant',
  'oracle': 'AI assistant',
  // Live-Intelligence hero leaked architecture subtitle
  'Cognitive Brain Topology': 'System Health Overview',
  'cognitive brain topology': 'system health overview',
  'Real-time neural architecture health map': 'Real-time component health map',
  'neural architecture health map': 'component health map',
  // Dreaming scenario gallery
  'VAE Generated': 'AI Synthesized',
  'GAN Generated': 'AI Synthesized',
  'VAE generated': 'AI synthesized',
  'GAN generated': 'AI synthesized',
  // Meta-Orchestra weekly cycle step descriptors. Longer phrases sort
  // first by length-desc so they match before the bare tokens. (Note:
  // 'EffortRouter' and 'MAML outer loop' are already mapped earlier.)
  'EffortRouter decisions': 'routing decisions',
  'effortRouter': 'priority router',
  'MAML Outer Loop': 'Adaptive Learning Cycle',
  'FAISS compression': 'vector compression',
  'FAISS vectors': 'vector store',
  'FAISS': 'vector store',
  'Thinking-Depth Router': 'Priority Router',
  'thinking-depth router': 'priority router',
  // Video analysis architecture leak
  'vision-LLM fusion': 'AI vision with context awareness',
  'temporal attention fusion': 'multi-frame analysis',
  // Self-Evolution take-away copy
  "brain's public report card": "AI improvement ledger",
  "brain's autonomous": 'the AI\'s autonomous',
  'The brain\'s': 'The AI\'s',
  'the brain\'s': 'the AI\'s',
  // Reasoning + KB internal terms (Neuro-Symbolic + Physics/Causal Engine
  // already mapped earlier in the dictionary — only new terms here).
  '8-Type Reasoning Engine': 'Multi-Mode Reasoning',
  'Neuro-Symbolic Rule': 'Rule-Guided AI',
  'Hybrid reasoning': 'AI reasoning',
  'AI brain status': 'AI engine status',
  'Memory System': 'Experience Store',
  // ─── Phase 12 (May 6 2026) — LoRA / AdaptiveTrigger / Loop B IP scrub ──
  // The architecture-revealing terms ("AdaptiveTrigger Loop B", "KB → LoRA
  // retrain", "LoRA fine-tune") leaked into empty states and tooltips on
  // /learning, /learning/lora-adapters, FederatedLearning. Mask them all.
  // Length-desc sort handles longer phrases before bare tokens.
  'AdaptiveTrigger Loop B (KB → LoRA retrain)': 'Asset-Adapter Pipeline',
  'AdaptiveTrigger Loop B': 'Asset-Adapter Pipeline',
  'AdaptiveTrigger Loop A': 'Knowledge Pipeline',
  'AdaptiveTrigger Loop': 'Asset-Adapter Pipeline',
  'AdaptiveTrigger': 'Asset-Adapter Pipeline',
  'KB → LoRA retrain': 'Asset-adapter retrain',
  'KB→LoRA retrain': 'Asset-adapter retrain',
  'KB → LoRA': 'Asset adapter',
  'KB→LoRA': 'Asset adapter',
  'LoRA Adapter Map': 'Asset-Specific Adapters',
  'LoRA adapter map': 'asset-specific adapters',
  'LoRA Adapter Federation': 'Adapter Federation',
  'LoRA Adapter Management': 'Asset Adapter Management',
  'LoRA Adapter': 'Asset Adapter',
  'LoRA Aggregation': 'Adapter Aggregation',
  'LoRA Rebuild': 'Adapter Rebuild',
  'LoRA Nightly Rebuild': 'Adapter Rebuild',
  'LoRA Status': 'Adapter Status',
  'LoRA fine-tune jobs': 'adapter fine-tune jobs',
  'LoRA fine-tune': 'adapter fine-tune',
  'LoRA nightly rebuild': 'adapter rebuild',
  'LoRA rebuild': 'adapter rebuild',
  'LoRA preference pairs': 'adapter preference pairs',
  'LoRA training': 'adapter training',
  'LoRA adapters': 'asset adapters',
  'LoRA adapter': 'asset adapter',
  'Federated LoRA': 'Federated Adapter',
  'federated LoRA': 'federated adapter',
  'LoRA': 'Asset Adapter',
  // Loop B / Loop A specifically (leaked in empty states + Self-Learning
  // page descriptions). "Loop X" in the architecture means a continual-
  // training cycle — neutral phrasing for users.
  'Loop B (KB → LoRA retrain)': 'Asset-adapter cycle',
  'Loop A (KB ingestion)': 'Knowledge ingestion cycle',
  'Loop B': 'Asset-adapter cycle',
  'Loop A': 'Knowledge ingestion cycle',
}

/**
 * Mask a single term if running on Spoke.
 * Optionally provide an explicit replacement instead of using TERM_MAP.
 */
export function maskTerm(term: string, isSpoke: boolean): string
export function maskTerm(term: string, replacement: string, isSpoke: boolean): string
export function maskTerm(term: string, secondArg: boolean | string, thirdArg?: boolean): string {
  if (typeof secondArg === 'boolean') {
    // 2-arg form: maskTerm(term, isSpoke)
    if (!secondArg) return term
    return TERM_MAP[term] ?? term
  }
  // 3-arg form: maskTerm(term, replacement, isSpoke)
  if (!thirdArg) return term
  return secondArg
}

/**
 * Mask all known IP terms in a text string if running on Spoke.
 * Replaces all occurrences of architecture terms with safe equivalents.
 */
export function maskText(text: string, isSpoke: boolean): string {
  if (!isSpoke || !text) return text

  let result = text
  // Sort by length descending to match longer phrases first
  const sortedKeys = Object.keys(TERM_MAP).sort((a, b) => b.length - a.length)
  for (const key of sortedKeys) {
    if (result.includes(key)) {
      result = result.split(key).join(TERM_MAP[key])
    }
  }
  return result
}
